/**
 * Kiểm hàng khi nhận, khiếu nại NCC, đánh giá NCC theo chất lượng (nhánh E Xuất nhập khẩu, DEC-555, docs/phases/P10/xnk-kiem-hang.md).
 * Bảng: trade_inspections / trade_inspection_lines / trade_inspection_photos / supplier_claims (migration nền 0240).
 */
import type { TradeDocKind, TradeLang } from './trade';

// ───────────────────────── Phiếu kiểm ─────────────────────────

export const INSPECTION_RESULTS = ['pending', 'pass', 'pass_note', 'fail'] as const;
export type InspectionResult = (typeof INSPECTION_RESULTS)[number];
export const INSPECTION_RESULT_LABELS: Record<InspectionResult, string> = {
  pending: 'Đang kiểm',
  pass: 'Đạt',
  pass_note: 'Đạt có ghi chú',
  fail: 'Không đạt',
};

export const DEFECT_TYPES = [
  'thickness',
  'dimension',
  'weight',
  'rust',
  'dent',
  'surface',
  'grade',
  'label',
  'missing',
  'wrong_item',
  'damaged',
  'other',
] as const;
export type DefectType = (typeof DEFECT_TYPES)[number];
export const DEFECT_TYPE_LABELS: Record<DefectType, string> = {
  thickness: 'Thiếu độ dày',
  dimension: 'Sai kích thước',
  weight: 'Thiếu trọng lượng',
  rust: 'Rỉ sét',
  dent: 'Móp, cong vênh',
  surface: 'Lỗi bề mặt / mạ',
  grade: 'Sai mác / tiêu chuẩn',
  label: 'Thiếu tem, nhãn, CO/CQ',
  missing: 'Thiếu số lượng',
  wrong_item: 'Giao sai hàng',
  damaged: 'Hư hỏng, vỡ',
  other: 'Khác',
};

/** Ảnh kiểm hàng: nén trên điện thoại trước khi gửi, máy chủ nhận tối đa 5 MB / ảnh (CHECK ở bảng), tối đa 40 ảnh / phiếu. */
export const INSPECTION_PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const INSPECTION_PHOTOS_PER_INSPECTION = 40;

/** Nhóm phiếu kiểm theo nhóm hàng (product_groups.attr_group, DEC-060). */
export const CHECKLIST_GROUPS = ['steel_pipe', 'steel_section', 'stainless', 'pipe_fitting', 'fire_protection', 'plastic_pipe', 'mep', 'general'] as const;
export type ChecklistGroup = (typeof CHECKLIST_GROUPS)[number];

export interface ChecklistItem {
  key: string;
  label: string;
  /** measure = đo (danh nghĩa + các lần đo); check = đạt / không */
  kind: 'measure' | 'check';
  unit?: string;
  /** Dung sai mặc định so với danh nghĩa (%), [âm, dương]. Mặc định chung — hợp đồng / tiêu chuẩn khác thì sửa ở phiếu. */
  tolPct?: readonly [number, number];
  /** Lỗi gợi ý khi mục này không đạt */
  defect: DefectType;
}

const m = (key: string, label: string, unit: string, tolPct: readonly [number, number], defect: DefectType): ChecklistItem => ({
  key,
  label,
  kind: 'measure',
  unit,
  tolPct,
  defect,
});
const c = (key: string, label: string, defect: DefectType): ChecklistItem => ({ key, label, kind: 'check', defect });

const COMMON_DOCS = c('docs', 'Tem nhãn, số lô, CO/CQ / mill test khớp hàng', 'label');

/**
 * Phiếu kiểm theo nhóm hàng. Dung sai là mức thường gặp (ống thép hàn: dày −10%, kg/cây −3,5 / +10% như ASTM A53;
 * thép hình/tấm: dày −5%). Kho chỉ nhập số đo, máy tự tô đỏ khi vượt dung sai.
 */
export const INSPECTION_CHECKLISTS: Record<ChecklistGroup, { label: string; items: readonly ChecklistItem[] }> = {
  steel_pipe: {
    label: 'Ống thép, thép hộp',
    items: [
      m('thickness_mm', 'Độ dày thành', 'mm', [-10, 10], 'thickness'),
      m('od_mm', 'Đường kính ngoài / cạnh', 'mm', [-1, 1], 'dimension'),
      m('length_m', 'Chiều dài cây', 'm', [0, 1], 'dimension'),
      m('kg_per_bar', 'Trọng lượng / cây', 'kg', [-3.5, 10], 'weight'),
      c('rust', 'Không rỉ, không bong mạ', 'rust'),
      c('straight', 'Thẳng, không móp méo, đầu cắt phẳng', 'dent'),
      COMMON_DOCS,
    ],
  },
  steel_section: {
    label: 'Thép hình, thép tấm',
    items: [
      m('thickness_mm', 'Độ dày (cánh / bụng / tấm)', 'mm', [-5, 10], 'thickness'),
      m('width_mm', 'Chiều rộng / chiều cao', 'mm', [-1, 1], 'dimension'),
      m('length_m', 'Chiều dài', 'm', [0, 1], 'dimension'),
      m('kg_per_bar', 'Trọng lượng / cây, tấm', 'kg', [-5, 5], 'weight'),
      c('rust', 'Không rỉ lỗ, không cán lỗi', 'rust'),
      COMMON_DOCS,
    ],
  },
  stainless: {
    label: 'Inox',
    items: [
      m('thickness_mm', 'Độ dày', 'mm', [-10, 10], 'thickness'),
      m('od_mm', 'Đường kính / khổ', 'mm', [-1, 1], 'dimension'),
      c('grade', 'Đúng mác (304 / 316 / 201…), thử thuốc hoặc máy phân tích', 'grade'),
      c('surface', 'Bề mặt (BA / 2B / HL) không xước, không ố', 'surface'),
      COMMON_DOCS,
    ],
  },
  pipe_fitting: {
    label: 'Phụ kiện, van, mặt bích',
    items: [
      c('size', 'Đúng cỡ DN, áp lực (PN / Class), kiểu nối', 'dimension'),
      c('thread', 'Ren / mặt bích không sứt, không nứt', 'damaged'),
      c('marking', 'Đúc chữ hãng, cỡ, áp lực, vật liệu', 'label'),
      COMMON_DOCS,
    ],
  },
  fire_protection: {
    label: 'Thiết bị PCCC',
    items: [
      c('stamp', 'Tem kiểm định PCCC còn hạn, đúng model', 'label'),
      c('model', 'Đúng model, thông số (nhiệt độ sprinkler, áp, lưu lượng)', 'wrong_item'),
      c('intact', 'Không vỡ bóng, không móp, đủ phụ kiện', 'damaged'),
      COMMON_DOCS,
    ],
  },
  plastic_pipe: {
    label: 'Ống nhựa, phụ kiện nhựa',
    items: [
      m('thickness_mm', 'Độ dày thành', 'mm', [0, 15], 'thickness'),
      m('od_mm', 'Đường kính ngoài', 'mm', [0, 1], 'dimension'),
      c('pn', 'Đúng PN / SDR in trên ống', 'grade'),
      c('surface', 'Không nứt, không phồng, màu đều', 'surface'),
      COMMON_DOCS,
    ],
  },
  mep: {
    label: 'Vật tư điện nước',
    items: [c('model', 'Đúng mã, thông số in trên hàng', 'wrong_item'), c('intact', 'Nguyên hộp, không vỡ', 'damaged'), COMMON_DOCS],
  },
  general: {
    label: 'Hàng khác',
    items: [c('model', 'Đúng hàng, đúng quy cách', 'wrong_item'), c('intact', 'Không hư hỏng', 'damaged'), COMMON_DOCS],
  },
};

/** Nhóm phiếu kiểm: theo nhóm hàng ERP; chưa gắn nhóm thì đoán theo tên. */
export function checklistGroupOf(attrGroup: string | null | undefined, name = ''): ChecklistGroup {
  if (attrGroup && (CHECKLIST_GROUPS as readonly string[]).includes(attrGroup)) return attrGroup as ChecklistGroup;
  const n = name.toLowerCase();
  if (/inox|stainless|sus ?3\d\d|\b30[14]\b|\b316\b|\b201\b/.test(n)) return 'stainless';
  if (/pccc|sprinkler|bình chữa cháy|đầu phun|tủ chữa cháy|lăng|họng/.test(n)) return 'fire_protection';
  if (/upvc|ppr|hdpe|\bpvc\b|ống nhựa/.test(n)) return 'plastic_pipe';
  if (/\bvan\b|co |tê |bích|măng sông|kép|rắc co|cút|nối/.test(`${n} `)) return 'pipe_fitting';
  if (/thép hình|thép tấm|\b[hiuv]\d{2,3}\b|thép góc|tôn|tấm/.test(n)) return 'steel_section';
  if (/ống|hộp|thép/.test(n)) return 'steel_pipe';
  if (/dây điện|cáp|aptomat|ổ cắm|đèn/.test(n)) return 'mep';
  return 'general';
}

/** Số đo của 1 mục: danh nghĩa + các lần đo; ok cho mục "check". */
export interface MeasureEntry {
  nominal?: number | null;
  values?: number[];
  /** Dung sai sửa ở phiếu (theo hợp đồng); trống = mặc định của mục */
  tolPct?: [number, number] | null;
  ok?: boolean | null;
}
export interface LineMeasurements {
  group: ChecklistGroup;
  items: Record<string, MeasureEntry>;
}

/** Sai lệch lớn nhất so với danh nghĩa (%), và có vượt dung sai không. Chưa đủ số → null. */
export function measureDeviation(e: MeasureEntry, def: ChecklistItem): { minPct: number; maxPct: number; out: boolean } | null {
  const vals = (e.values ?? []).filter((v) => Number.isFinite(v));
  if (!e.nominal || e.nominal <= 0 || !vals.length) return null;
  const pcts = vals.map((v) => Math.round(((v - e.nominal!) / e.nominal!) * 1000) / 10);
  const [lo, hi] = e.tolPct ?? def.tolPct ?? [-Infinity, Infinity];
  const minPct = Math.min(...pcts);
  const maxPct = Math.max(...pcts);
  return { minPct, maxPct, out: minPct < lo || maxPct > hi };
}

/** Các mục không đạt của 1 dòng (đo vượt dung sai hoặc tick "không đạt"). */
export function failedChecklistItems(ms: LineMeasurements | null | undefined): ChecklistItem[] {
  if (!ms) return [];
  const list = INSPECTION_CHECKLISTS[ms.group]?.items ?? [];
  return list.filter((it) => {
    const e = ms.items?.[it.key];
    if (!e) return false;
    if (it.kind === 'check') return e.ok === false;
    return measureDeviation(e, it)?.out === true;
  });
}

/**
 * Kết quả được phép chốt theo các dòng: có hàng lỗi → "Không đạt" hoặc "Đạt có ghi chú" (nhận hàng, khiếu nại giảm giá);
 * không có hàng lỗi → "Đạt" hoặc "Đạt có ghi chú". Gợi ý = mục đầu.
 */
export function allowedInspectionResults(lines: { qtyFailed: number; defectType?: string | null; note?: string | null }[]): InspectionResult[] {
  if (lines.some((l) => l.qtyFailed > 0)) return ['fail', 'pass_note'];
  if (lines.some((l) => l.defectType || (l.note && l.note.trim()))) return ['pass_note', 'pass'];
  return ['pass', 'pass_note'];
}

// ───────────────────────── Khiếu nại NCC ─────────────────────────

export const SUPPLIER_CLAIM_STATUSES = ['open', 'sent', 'replied', 'settled', 'rejected', 'closed'] as const;
export type SupplierClaimStatus = (typeof SUPPLIER_CLAIM_STATUSES)[number];
export const SUPPLIER_CLAIM_STATUS_LABELS: Record<SupplierClaimStatus, string> = {
  open: 'Mới lập',
  sent: 'Đã gửi NCC',
  replied: 'NCC đã trả lời',
  settled: 'Đã giải quyết',
  rejected: 'NCC từ chối',
  closed: 'Đã đóng',
};
/** Chuyển trạng thái hợp lệ. "Đã đóng" = bỏ qua / thôi theo đuổi; settled / rejected vẫn đóng được sau. */
export const SUPPLIER_CLAIM_NEXT: Record<SupplierClaimStatus, readonly SupplierClaimStatus[]> = {
  open: ['sent', 'closed'],
  sent: ['replied', 'settled', 'rejected', 'closed'],
  replied: ['settled', 'rejected', 'sent', 'closed'],
  settled: ['closed'],
  rejected: ['sent', 'closed'],
  closed: [],
};
export const SUPPLIER_CLAIM_OPEN: readonly SupplierClaimStatus[] = ['open', 'sent', 'replied'];

export const SUPPLIER_CLAIM_TYPES = ['discount', 'replace', 'next_lot', 'refund', 'return', 'other'] as const;
export type SupplierClaimType = (typeof SUPPLIER_CLAIM_TYPES)[number];
export const SUPPLIER_CLAIM_TYPE_LABELS: Record<SupplierClaimType, string> = {
  discount: 'Giảm giá',
  replace: 'Đổi hàng',
  next_lot: 'Bù vào lô sau',
  refund: 'Hoàn tiền',
  return: 'Trả hàng',
  other: 'Khác',
};

/** Hạn khiếu nại mặc định khi hợp đồng không ghi: 30 ngày từ ngày hàng về (tàu đến) hoặc ngày kiểm. */
export const CLAIM_DEFAULT_DAYS = 30;
/** Khiếu nại còn mở quá số ngày này bị trừ điểm chất lượng NCC (supplierScoreV2). */
export const CLAIM_STALE_DAYS = 30;

/** Còn bao nhiêu ngày tới hạn khiếu nại; ≤ 7 ngày = gấp. */
export function claimDeadlineStatus(deadline: string | null, today: string): { daysLeft: number; level: 'ok' | 'soon' | 'overdue' } | null {
  if (!deadline) return null;
  const daysLeft = Math.round((Date.parse(`${deadline.slice(0, 10)}T00:00:00Z`) - Date.parse(`${today.slice(0, 10)}T00:00:00Z`)) / 86_400_000);
  return { daysLeft, level: daysLeft < 0 ? 'overdue' : daysLeft <= 7 ? 'soon' : 'ok' };
}

/** Chứng từ NCC phải gửi trước khi tàu đến (điểm "chứng từ" của NCC). Tờ khai do Minh Tiến làm nên không tính. */
export const SUPPLIER_DOCS_BEFORE_ARRIVAL: readonly TradeDocKind[] = ['commercial_invoice', 'packing_list', 'bill_of_lading'];

// ───────────────────────── Thư khiếu nại mẫu (không AI) ─────────────────────────

export interface ClaimLetterInput {
  supplierName: string;
  claimCode: string;
  refs: string[];
  /** Mỗi dòng: "Mã hàng – tên: 12/200 cây không đạt – Thiếu độ dày (đo 1,62 / 1,80 mm)" */
  defects: string[];
  claimType: SupplierClaimType;
  amount: string | null;
  deadline: string | null;
  photoCount: number;
  senderName: string;
}

const CLAIM_ASK: Record<TradeLang, Record<SupplierClaimType, string>> = {
  vi: {
    discount: 'giảm giá cho phần hàng không đạt',
    replace: 'đổi hàng đạt chất lượng',
    next_lot: 'bù hàng vào lô kế tiếp',
    refund: 'hoàn tiền phần hàng không đạt',
    return: 'nhận lại hàng không đạt',
    other: 'phương án xử lý',
  },
  en: {
    discount: 'a price discount for the non-conforming goods',
    replace: 'replacement with conforming goods',
    next_lot: 'compensation in the next shipment',
    refund: 'a refund for the non-conforming goods',
    return: 'taking back the non-conforming goods',
    other: 'your proposed settlement',
  },
  zh: {
    discount: '对不合格货物给予降价补偿',
    replace: '更换合格货物',
    next_lot: '在下一批货中补偿',
    refund: '退还不合格货物的货款',
    return: '退回不合格货物',
    other: '贵司的处理方案',
  },
  ja: {
    discount: '不適合品の値引き',
    replace: '適合品との交換',
    next_lot: '次回出荷での補償',
    refund: '不適合品代金の返金',
    return: '不適合品の返品受入れ',
    other: '貴社の対応案',
  },
  ko: {
    discount: '불량품에 대한 가격 할인',
    replace: '양품으로 교환',
    next_lot: '다음 선적분에서 보상',
    refund: '불량품 대금 환불',
    return: '불량품 반품 수용',
    other: '귀사의 처리 방안',
  },
};

/** Thư khiếu nại soạn sẵn theo tiếng NCC (dùng khi chưa bật AI hoặc AI lỗi). */
export function claimLetterTemplate(lang: TradeLang, i: ClaimLetterInput): { subject: string; body: string } {
  const refs = i.refs.filter(Boolean).join(', ');
  const list = i.defects.map((d) => `- ${d}`).join('\n');
  const ask = CLAIM_ASK[lang][i.claimType];
  switch (lang) {
    case 'en':
      return {
        subject: `Quality claim ${i.claimCode}${refs ? ` – ${refs}` : ''}`,
        body: `Dear ${i.supplierName},\n\nUpon receipt and inspection of the goods under ${refs || 'our order'}, we found the following non-conformities:\n${list}\n\n${i.photoCount ? `${i.photoCount} photos taken at our warehouse are attached. ` : ''}The goods concerned are being held separately and are available for your inspection.\n\nAccording to the claim clause of our contract, we kindly request ${ask}${i.amount ? ` in the amount of ${i.amount}` : ''}.${i.deadline ? ` Please note the claim period ends on ${i.deadline}.` : ''} We look forward to your reply within 7 days.\n\nBest regards,\n${i.senderName}\nMinh Tien Steel & Industrial Equipment Import Export Co., Ltd.`,
      };
    case 'zh':
      return {
        subject: `质量索赔 ${i.claimCode}${refs ? ` – ${refs}` : ''}`,
        body: `${i.supplierName} 您好：\n\n我司收到${refs || '订单'}项下货物并验货后，发现以下不合格情况：\n${list}\n\n${i.photoCount ? `附上仓库现场照片 ${i.photoCount} 张。` : ''}相关货物已单独存放，可供贵司查验。\n\n根据合同索赔条款，请贵司${ask}${i.amount ? `，金额 ${i.amount}` : ''}。${i.deadline ? `索赔期限至 ${i.deadline}。` : ''}请于 7 天内回复。\n\n此致\n${i.senderName}\n明进钢铁及工业设备进出口有限公司`,
      };
    case 'ja':
      return {
        subject: `品質クレーム ${i.claimCode}${refs ? ` – ${refs}` : ''}`,
        body: `${i.supplierName} 御中\n\n${refs || 'ご注文'}の貨物を受領・検品した結果、以下の不適合が判明しました。\n${list}\n\n${i.photoCount ? `倉庫で撮影した写真 ${i.photoCount} 枚を添付します。` : ''}該当品は別保管しており、ご確認いただけます。\n\n契約のクレーム条項に基づき、${ask}をお願い申し上げます${i.amount ? `（金額 ${i.amount}）` : ''}。${i.deadline ? `クレーム期限は ${i.deadline} です。` : ''}7日以内にご回答ください。\n\n敬具\n${i.senderName}\nMinh Tien Steel & Industrial Equipment Import Export Co., Ltd.`,
      };
    case 'ko':
      return {
        subject: `품질 클레임 ${i.claimCode}${refs ? ` – ${refs}` : ''}`,
        body: `${i.supplierName} 귀중\n\n${refs || '주문'} 건 화물을 입고·검수한 결과 다음과 같은 불량이 확인되었습니다.\n${list}\n\n${i.photoCount ? `창고에서 촬영한 사진 ${i.photoCount}장을 첨부합니다. ` : ''}해당 화물은 별도 보관 중이며 확인 가능합니다.\n\n계약서의 클레임 조항에 따라 ${ask}${i.amount ? `(금액 ${i.amount})` : ''}을 요청드립니다.${i.deadline ? ` 클레임 기한은 ${i.deadline}입니다.` : ''} 7일 이내 회신 부탁드립니다.\n\n감사합니다.\n${i.senderName}\nMinh Tien Steel & Industrial Equipment Import Export Co., Ltd.`,
      };
    default:
      return {
        subject: `Khiếu nại chất lượng ${i.claimCode}${refs ? ` – ${refs}` : ''}`,
        body: `Kính gửi ${i.supplierName},\n\nKhi nhận và kiểm hàng của ${refs || 'đơn hàng'}, Minh Tiến phát hiện:\n${list}\n\n${i.photoCount ? `Gửi kèm ${i.photoCount} ảnh chụp tại kho. ` : ''}Phần hàng này đang được để riêng, quý công ty có thể cử người kiểm tra.\n\nTheo điều khoản khiếu nại của hợp đồng, đề nghị quý công ty ${ask}${i.amount ? ` với số tiền ${i.amount}` : ''}.${i.deadline ? ` Hạn khiếu nại tới ngày ${i.deadline}.` : ''} Mong nhận phản hồi trong 7 ngày.\n\nTrân trọng,\n${i.senderName}\nCông ty TNHH XNK Thép & Thiết bị Công nghiệp Minh Tiến`,
      };
  }
}
